import React, { useEffect } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { useQzTrayConnection } from "../../../hooks/qztray/useQzTrayConnection";

/**
 * 🔒 ConnectionSecurityBox
 *
 * Muestra si la conexión con el cliente QZ Tray es segura (SSL/WSS activado).
 * Compatible con modo local y modo nube.
 */
const ConnectionSecurityBox: React.FC = () => {
  const { status, fetchStatus } = useQzTrayConnection();

  // 🚀 Obtener el estado inicial del cliente QZ Tray
  useEffect(() => {
    fetchStatus();
  }, []);

  // ===========================================================
  // 🧠 Determinar si SSL está activo
  // - Para QZ Tray usamos "wss://" como indicador de seguridad
  // ===========================================================
  const endpoint = status?.endpoint?.toLowerCase() ?? "";
  const sslEnabled = endpoint.startsWith("wss://") || endpoint.startsWith("https://");

  // 🟢 Etiqueta descriptiva según el modo
  const securityDesc = sslEnabled
    ? "La comunicación con QZ Tray está cifrada."
    : "La comunicación no está cifrada (modo local).";

  return (
    <div className="rounded-sm border border-stroke bg-white dark:border-strokedark dark:bg-boxdark shadow-md p-5 flex flex-col items-center justify-center transition-colors">
      {/* 🔹 Encabezado con icono */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${
            sslEnabled
              ? "bg-green-100 dark:bg-green-900/40"
              : "bg-yellow-100 dark:bg-yellow-900/40"
          }`}
        >
          {sslEnabled ? (
            <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
          ) : (
            <ShieldAlert className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          )}
        </div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Seguridad SSL
        </h4>
      </div>

      {/* 🔸 Estado principal */}
      <span
        className={`text-base font-bold ${
          sslEnabled
            ? "text-green-600 dark:text-green-400"
            : "text-yellow-600 dark:text-yellow-400"
        }`}
      >
        {sslEnabled ? "Activada" : "Desactivada"}
      </span>

      {/* 🔸 Descripción breve */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
        {securityDesc}
      </p>

      {/* 🔸 Endpoint actual */}
      {status?.endpoint && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 truncate max-w-full">
          {status.endpoint}
        </p>
      )}
    </div>
  );
};

export default ConnectionSecurityBox;
